// pages/DigitalLibraryPage.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Tab,
  Tabs,
  Paper,
} from "@mui/material";
import {
  School,
  Search,
  MenuBook,
  Article,
  VideoLibrary,
  PictureAsPdf,
  ExitToApp,
  Home,
  Download,
  Visibility,
  Favorite,
  FavoriteBorder,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useAuth } from "../contexts/AuthContext";
import { getAnimalAvatar, getDisplayUsername } from "../utils/userUtils";

// Academic Theme
const academicTheme = createTheme({
  palette: {
    primary: {
      main: "#1565C0",
    },
    secondary: {
      main: "#FF8F00",
    },
    background: {
      default: "#F8F9FA",
      paper: "#FFFFFF",
    },
  },
});

// Mock library data
const libraryItems = [
  {
    id: 1,
    title: "การวิเคราะห์ข้อมูลขั้นสูง",
    type: "book",
    author: "ดร.สมชาย วิเคราะห์",
    category: "วิทยาศาสตร์ข้อมูล",
    thumbnail: "/course-placeholder.svg",
    downloads: 1250,
    views: 3420,
    favorite: false,
    description:
      "หนังสือเรียนวิเคราะห์ข้อมูลขั้นสูงสำหรับนักศึกษาระดับปริญญาโท",
  },
  {
    id: 2,
    title: "Machine Learning in Practice",
    type: "video",
    author: "Prof. John Smith",
    category: "ปัญญาประดิษฐ์",
    thumbnail: "/course-placeholder.svg",
    downloads: 890,
    views: 2150,
    favorite: true,
    description:
      "วิดีโอบรรยายเกี่ยวกับการประยุกต์ใช้ Machine Learning ในงานจริง",
  },
  {
    id: 3,
    title: "งานวิจัยด้านไซเบอร์ซิเคียวริตี้",
    type: "research",
    author: "ทีมวิจัย ITU",
    category: "ความปลอดภัยไซเบอร์",
    thumbnail: "/course-placeholder.svg",
    downloads: 567,
    views: 1890,
    favorite: false,
    description: "รายงานการวิจัยด้านความปลอดภัยไซเบอร์ปี 2024",
  },
  {
    id: 4,
    title: "คู่มือการพัฒนาเว็บแอปพลิเคชัน",
    type: "book",
    author: "อ.ธนาธิป โปรแกรม",
    category: "การพัฒนาซอฟต์แวร์",
    thumbnail: "/course-placeholder.svg",
    downloads: 2100,
    views: 4320,
    favorite: true,
    description: "คู่มือสำหรับการพัฒนาเว็บแอปพลิเคชันด้วยเทคโนโลยีสมัยใหม่",
  },
  {
    id: 5,
    title: "IoT และสมาร์ทซิตี้",
    type: "video",
    author: "ศ.ดร.วิทยา อินเทอร์เน็ต",
    category: "อินเทอร์เน็ตของสรรพสิ่ง",
    thumbnail: "/course-placeholder.svg",
    downloads: 745,
    views: 1950,
    favorite: false,
    description: "บรรยายพิเศษเกี่ยวกับการพัฒนาสมาร์ทซิตี้ด้วยเทคโนโลยี IoT",
  },
  {
    id: 6,
    title: "Blockchain และคริปโตเคอเรนซี",
    type: "research",
    author: "หน่วยวิจัยฟินเทค",
    category: "เทคโนโลยีการเงิน",
    thumbnail: "/course-placeholder.svg",
    downloads: 412,
    views: 1200,
    favorite: false,
    description:
      "การศึกษาเทคโนโลยี Blockchain และการประยุกต์ใช้ในอุตสาหกรรมการเงิน",
  },
];

const DigitalLibraryPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);
  const [favorites, setFavorites] = useState(new Set([2, 4]));

  const username = user?.username || "ผู้ใช้ระบบ";
  const displayName = getDisplayUsername(username, "short_student_id");
  const animalAvatar = getAnimalAvatar(username);

  const handleLogout = () => {
    logout();
    navigate("/academic");
  };

  const toggleFavorite = (id) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(id)) {
        newFavorites.delete(id);
      } else {
        newFavorites.add(id);
      }
      return newFavorites;
    });
  };

  const filteredItems = libraryItems.filter((item) => {
    const matchesSearch = item.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesTab =
      selectedTab === 0 ||
      (selectedTab === 1 && item.type === "book") ||
      (selectedTab === 2 && item.type === "video") ||
      (selectedTab === 3 && item.type === "research");
    return matchesSearch && matchesTab;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case "book":
        return <MenuBook />;
      case "video":
        return <VideoLibrary />;
      case "research":
        return <Article />;
      default:
        return <PictureAsPdf />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "book":
        return "หนังสือ";
      case "video":
        return "วิดีโอ";
      case "research":
        return "งานวิจัย";
      default:
        return "เอกสาร";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "book":
        return "#2E7D32";
      case "video":
        return "#D32F2F";
      case "research":
        return "#FF8F00";
      default:
        return "#757575";
    }
  };

  return (
    <ThemeProvider theme={academicTheme}>
      <Box sx={{ minHeight: "100vh", backgroundColor: "#F8F9FA" }}>
        {/* Navigation Bar */}
        <AppBar position="static" sx={{ backgroundColor: "#1565C0" }}>
          <Toolbar>
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <IconButton
                onClick={() => navigate("/dashboard")}
                sx={{ color: "white", mr: 2 }}
              >
                <School sx={{ fontSize: 32 }} />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                ห้องสมุดดิจิตอล
              </Typography>
            </Box>

            <Button
              startIcon={<Home />}
              sx={{ color: "white", mr: 2 }}
              onClick={() => navigate("/dashboard")}
            >
              หน้าหลัก
            </Button>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar sx={{ bgcolor: "#FF8F00", mr: 1 }}>{animalAvatar}</Avatar>
              <Typography variant="body2" sx={{ color: "white", mr: 2 }}>
                {displayName}
              </Typography>
              <IconButton onClick={handleLogout} sx={{ color: "white" }}>
                <ExitToApp />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: "#1565C0",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MenuBook sx={{ fontSize: 48, mr: 2 }} />
              ห้องสมุดดิจิตอล
            </Typography>
            <Typography variant="h6" sx={{ color: "#666", mb: 4 }}>
              คลังความรู้ออนไลน์สำหรับนักศึกษาและบุคลากร
            </Typography>

            {/* Search */}
            <Paper sx={{ p: 1, maxWidth: 600, mx: "auto" }}>
              <TextField
                fullWidth
                placeholder="ค้นหาหนังสือ วิดีโอ งานวิจัย..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#666" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& fieldset": { border: "none" } }}
              />
            </Paper>
          </Box>

          {/* Tabs */}
          <Box sx={{ mb: 4 }}>
            <Tabs
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              centered
              sx={{
                "& .MuiTab-root": {
                  fontWeight: 600,
                  fontSize: "1rem",
                },
              }}
            >
              <Tab label="ทั้งหมด" />
              <Tab label="หนังสือ" />
              <Tab label="วิดีโอ" />
              <Tab label="งานวิจัย" />
            </Tabs>
          </Box>

          {/* Library Items */}
          <Grid container spacing={3}>
            {filteredItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 8px 24px rgba(21,101,192,0.15)",
                    },
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      height="160"
                      image={item.thumbnail}
                      alt={item.title}
                      sx={{ objectFit: "cover" }}
                    />
                    <Chip
                      icon={getTypeIcon(item.type)}
                      label={getTypeLabel(item.type)}
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        backgroundColor: getTypeColor(item.type),
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                    <IconButton
                      onClick={() => toggleFavorite(item.id)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,1)",
                        },
                      }}
                    >
                      {favorites.has(item.id) ? (
                        <Favorite sx={{ color: "#D32F2F" }} />
                      ) : (
                        <FavoriteBorder />
                      )}
                    </IconButton>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: "#1565C0",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                      โดย: {item.author}
                    </Typography>

                    <Chip
                      label={item.category}
                      size="small"
                      sx={{
                        backgroundColor: "#E3F2FD",
                        color: "#1565C0",
                        mb: 2,
                      }}
                    />

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        mb: 2,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {item.description}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Visibility sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="caption">
                            {item.views}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Download sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="caption">
                            {item.downloads}
                          </Typography>
                        </Box>
                      </Box>

                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Download />}
                        sx={{
                          backgroundColor: "#1565C0",
                          "&:hover": {
                            backgroundColor: "#0D47A1",
                          },
                        }}
                      >
                        ดาวน์โหลด
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filteredItems.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" sx={{ color: "#666" }}>
                ไม่พบรายการที่ค้นหา
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default DigitalLibraryPage;
